import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../components/utils/ResourceError";
import { ResourceEmpty } from "../../../components/utils/ResourceEmpty";
import { SaveBar } from "../../../components/utils/SaveBar";
import { fetchWorkOrder } from "../../../api/work-order";
import { itemListsApi } from "../../../api/item_lists";
import {
  BuildingOfficeIcon,
  MapPinIcon,
  PhoneIcon,
  UsersIcon,
  CubeIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import WorkOrderItemConsumptionList from "../../../components/dashboard/workOrders/work-order-item-consumption-list";
import WorkOrderItemHandlingList from "../../../components/dashboard/workOrders/work-order-item-handling-list";
import { CopyToClipboard } from "../../../components/utils/Div";
import { mapsServiceUrl } from "../../../components/Utils";
import { useWorkOrderPermissions } from "../../../routes/roleOnly";
import { getHoursAsync } from "../../../slices/hour";
import { TimeRegistrationSection } from "../../../components/dashboard/hours/time-registration-section";

export const WorkOrderItemConsumptionPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const settings = useSelector((state) => state.session.currentUser.settings);

  const [workOrder, setWorkOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [errors, setErrors] = useState([]);
  const [timeRegistrationDialogSignal, setTimeRegistrationDialogSignal] =
    useState(0);

  const permissions = useWorkOrderPermissions(workOrder);

  const hoursController = useMemo(
    () => ({
      query: "",
      page: 1,
      per_page: 100,
      sortBy: "created_at",
      sort: "desc",
      filters:
        workOrder?.id ? [{ name: "WORK_ORDER", value: workOrder.id }] : [],
      view: null,
      fields: "time,effective_hourly_rate",
    }),
    [workOrder?.id]
  );

  // Determine if user can edit this work order
  const canEdit = workOrder?.user_can_edit !== false;

  // Function to reload only item_lists without reloading entire work order
  const reloadItemLists = async () => {
    if (!id) return;
    
    try {
      // Fetch item_lists directly from item_lists API
      const response = await itemListsApi.getWorkOrderItemLists(id);
      const itemLists = response?.data?.data || [];
      
      // Update only item_lists in workOrder state
      setWorkOrder(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          item_lists: itemLists || []
        };
      });
    } catch (error) {
      console.error("Error reloading item lists:", error);
      // Don't show error toast, just log it
    }
  };

  // Load work order with optimized fields
  const loadWorkOrder = async () => {
    if (!id) return;

    setIsLoading(true);
    setError(false);
    setErrors([]);

    const params = {
      fields:
        "id,number,title,displayTitle,status,user_can_edit,customer.*,contact.*,project.*,delivery_address,project_activity,project_activity_id,items",
    };

    try {
      const response = await fetchWorkOrder(id, params);
      // Handle both response.data and response.data.data structures
      const data = response?.data?.data || response?.data || response;
      
      // Fetch item_lists separately from item_lists API
      try {
        const itemListsResponse = await itemListsApi.getWorkOrderItemLists(id);
        const itemLists = itemListsResponse?.data?.data || [];
        data.item_lists = itemLists;
      } catch (itemListsError) {
        console.error("Error loading item lists:", itemListsError);
        data.item_lists = [];
      }
      
      setWorkOrder(data);
      setIsLoading(false);
    } catch (error) {
      toast.error("Kunne ikke laste arbeidsordre");
      setError(true);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadWorkOrder();
  }, [id]);

  const handleOnRefresh = async () => {
    // Reload work order with same optimized fields
    await loadWorkOrder();
  };

  if (isLoading) {
    return <ResourceLoading size="base" />;
  }

  if (error) {
    return <ResourceError errors={errors} />;
  }

  if (!workOrder?.id) {
    return <ResourceEmpty />;
  }

  const headerMeta = [
    {
      label: workOrder?.customer?.customer_number + " " + workOrder?.customer?.name,
      description: "",
      path: "/dashboard/customers/" + workOrder?.customer?.id,
      action: null,
      icon: UsersIcon,
      textColor: "text-teal-700",
      hidden: workOrder?.customer?.id ? false : true,
      copy: false,
    },
    {
      label: workOrder?.project?.displayTitle,
      description: "",
      path: "/dashboard/projects/" + workOrder?.project?.id,
      action: null,
      icon: BuildingOfficeIcon,
      textColor: "text-teal-700",
      hidden: workOrder?.project?.id ? false : true,
      copy: false,
    },
    {
      label: (
        <CopyToClipboard
          path={mapsServiceUrl(
            workOrder?.delivery_address?.displayTitle || "",
            settings?.preferred_maps_service
          )}
          content={workOrder?.delivery_address?.displayTitle || ""}
          type="popup"
        />
      ),
      description: "",
      path: null,
      action: null,
      icon: MapPinIcon,
      textColor: "text-teal-700",
      hidden: workOrder?.delivery_address ? false : true,
      copy: true,
    },
    {
      label: (
        <CopyToClipboard
          path={"tel:" + workOrder?.customer?.mobile}
          content={workOrder?.customer?.mobile}
          type="popup"
        />
      ),
      description: "",
      path: null,
      action: null,
      icon: PhoneIcon,
      textColor: "text-teal-700",
      hidden: workOrder?.customer?.mobile ? false : true,
      copy: true,
    },
  ];

  return (
    <>
      <DashboardHeader
        subtitle="Vareforbruk"
        title={workOrder?.displayTitle || `${workOrder?.number} - ${workOrder?.title}`}
        meta={headerMeta}
      >
        <TimeRegistrationSection
          className="hidden"
          module="work_order"
          moduleId={workOrder.id}
          project={workOrder.project}
          workOrder={workOrder}
          onRefresh={() => {
            if (workOrder?.id) {
              const filters = [{ name: "WORK_ORDER", value: workOrder.id }];
              dispatch(getHoursAsync({ ...hoursController, filters }));
            }
          }}
          title="Timeregistrering"
          description="Registrer timer for denne arbeidsordren"
          openRegistrationDialogSignal={timeRegistrationDialogSignal}
        />
        <div className="mx-auto max-w-7xl px-4 py-5 sm:px-6 lg:px-8 pb-28 lg:pb-8">
          {/* Work Order Info Section */}
          <div className="mb-6 bg-white shadow sm:rounded-lg">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <CubeIcon className="h-6 w-6 text-teal-600 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Vareforbruk for arbeidsordre
                  </h2>
                  <p className="text-sm text-gray-500 mt-1 truncate">
                    {workOrder?.number} - {workOrder?.title}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Item Consumption Content */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 sm:px-6 py-4 sm:py-6">
              {!canEdit && (
                <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-md p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-yellow-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800">
                        Kun visning
                      </h3>
                      <div className="mt-2 text-sm text-yellow-700">
                        <p>
                          Vareforbruket kan ikke redigeres. Du kan se innholdet,
                          men ikke lagre endringer.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-6">
                <WorkOrderItemConsumptionList
                  items={[]}
                  onItemsChange={() => {}}
                  disabled={!canEdit}
                  workOrder={workOrder}
                  onWorkOrderUpdate={reloadItemLists}
                  onItemsUpdate={() => {}}
                />
              </div>
            </div>
          </div>
          
          {/* Item Handling List - shown when handling list exists */}
          <div className="bg-white shadow sm:rounded-lg mt-6">
            <div className="px-4 sm:px-6 py-4 sm:py-6">
              {workOrder?.item_lists && workOrder.item_lists.length > 0 && (
                <WorkOrderItemHandlingList
                  itemList={workOrder.item_lists.find(list => list.list_type === 'handling')}
                  isLoading={false}
                />
              )}
            </div>
          </div>
        </div>
        <nav
          className="lg:hidden fixed bottom-0 left-0 right-0 z-40 border-t border-gray-200 bg-white/95 shadow-[0_-4px_14px_rgba(0,0,0,0.06)] backdrop-blur-sm supports-[backdrop-filter]:bg-white/90"
          style={{
            paddingBottom: "max(0.35rem, env(safe-area-inset-bottom, 0px))",
          }}
          aria-label={t("work_order.view.mobileNav.ariaLabel")}
        >
          <div className="flex items-stretch justify-around gap-0 px-0.5 pt-1">
            <button
              type="button"
              disabled={!permissions.canItemConsumption}
              onClick={() =>
                navigate(`/dashboard/work_orders/${workOrder.id}/item-consumption`)
              }
              className="flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-lg px-0.5 py-1.5 text-teal-700 transition-colors hover:bg-gray-50 active:bg-gray-100 disabled:pointer-events-none disabled:opacity-40"
              aria-current="page"
            >
              <CubeIcon className="h-5 w-5 shrink-0" aria-hidden />
              <span className="max-w-full text-center text-[10px] font-medium leading-tight">
                {t("report.view.mobileNav.items")}
              </span>
            </button>
            <button
              type="button"
              onClick={() => setTimeRegistrationDialogSignal((n) => n + 1)}
              className="flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-lg px-0.5 py-1.5 text-gray-600 transition-colors hover:bg-gray-50 active:bg-gray-100"
            >
              <ClockIcon className="h-5 w-5 shrink-0" aria-hidden />
              <span className="max-w-full text-center text-[10px] font-medium leading-tight">
                {t("report.view.mobileNav.timeRegistration")}
              </span>
            </button>
          </div>
        </nav>
      </DashboardHeader>

      <SaveBar
        alwaysShow={true}
        className="max-lg:!bottom-[4.75rem]"
        onBack={() => navigate(`/dashboard/work_orders/${workOrder?.id}`)}
        backLabel="Tilbake til arbeidsordre"
      />
    </>
  );
};

